import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Timer,
  LayoutDashboard,
  Users,
  Plane,
  Settings,
  ArrowLeft,
  BarChart3,
  FileText,
  Bell,
  DollarSign,
  Globe,
  Briefcase,
  CreditCard,
  Landmark
} from 'lucide-react';
import { TTLManagementPanel } from './TTLManagementPanel';
import { BookingsPanel } from './BookingsPanel';
import { ReportsPanel } from './ReportsPanel';
import { NotificationsPanel } from './NotificationsPanel';
import { MarkupPanel } from './MarkupPanel';
import { UserManagementPanel } from './UserManagementPanel';
import { PNRRetrievalPanel } from './PNRRetrievalPanel';
import { CareersPanel } from './CareersPanel';
import { PaymentsPanel } from './PaymentsPanel';
import { UmrahBookingsPanel } from './UmrahBookingsPanel';

/**
 * Admin Dashboard - Main admin panel for Flight380 management
 */
export const AdminDashboard = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('bookings');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBack}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <LayoutDashboard className="h-6 w-6" />
                  Admin Dashboard
                </h1>
                <p className="text-blue-100 text-sm">Flight380 Management Console</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-green-500/20 text-green-100 rounded-full text-sm">
                ● System Online
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm border p-1 h-auto flex-wrap">
            <TabsTrigger 
              value="bookings" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
            >
              <Plane className="h-4 w-4" />
              Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="users" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
            >
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger 
              value="reports" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
            >
              <BarChart3 className="h-4 w-4" />
              Reports
            </TabsTrigger>
            <TabsTrigger 
              value="notifications" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
            >
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger
              value="payments"
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
              data-testid="admin-tab-payments"
            >
              <CreditCard className="h-4 w-4" />
              Payments
            </TabsTrigger>
            <TabsTrigger
              value="umrah-bookings"
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
              data-testid="admin-tab-umrah-bookings"
            >
              <Landmark className="h-4 w-4" />
              Umrah Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="markups" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
            >
              <DollarSign className="h-4 w-4" />
              Pricing & Markups
            </TabsTrigger>
            <TabsTrigger 
              value="ttl" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
            >
              <Timer className="h-4 w-4" />
              TTL Settings
            </TabsTrigger>
            <TabsTrigger 
              value="pnr-retrieval" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
            >
              <Globe className="h-4 w-4" />
              GDS Retrieval
            </TabsTrigger>
            <TabsTrigger
              value="careers"
              className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2"
              data-testid="admin-tab-careers"
            >
              <Briefcase className="h-4 w-4" />
              Careers
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="mt-6">
            <BookingsPanel />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <UserManagementPanel />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-6">
            <ReportsPanel />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-6">
            <NotificationsPanel />
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="mt-6">
            <PaymentsPanel />
          </TabsContent>

          <TabsContent value="umrah-bookings" className="mt-6">
            <UmrahBookingsPanel />
          </TabsContent>

          {/* Pricing & Markups Tab */}
          <TabsContent value="markups" className="mt-6">
            <MarkupPanel />
          </TabsContent>

          {/* TTL Management Tab */}
          <TabsContent value="ttl" className="mt-6">
            <TTLManagementPanel />
          </TabsContent>

          {/* GDS PNR Retrieval Tab */}
          <TabsContent value="pnr-retrieval" className="mt-6">
            <PNRRetrievalPanel />
          </TabsContent>

          {/* Careers Tab */}
          <TabsContent value="careers" className="mt-6">
            <CareersPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
